import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Calendar, Plus, Trash2, MessageSquare, CreditCard, ShieldCheck, Ban, Download, Zap, UserCheck } from "lucide-react";
import MessagingDrawer from "@/components/MessagingDrawer";
import RoomPhotosUploader from "@/components/RoomPhotosUploader";
import CoachLocationCard from "@/components/CoachLocationCard";
import WeekCalendar from "@/components/WeekCalendar";
import VerifiedBadge from "@/components/VerifiedBadge";
import CoachAvailabilityCard from "@/components/CoachAvailabilityCard";
import ManagerStats from "@/components/ManagerStats";
import FavoriteButton from "@/components/FavoriteButton";
import ApprovedCoaches from "@/components/ApprovedCoaches";
import LastMinuteSlots from "@/components/LastMinuteSlots";
import CalendarSyncCard from "@/components/CalendarSyncCard";
import PromoCodesCard from "@/components/PromoCodesCard";
import PayModal from "@/components/PayModal";
import PushToggle from "@/components/PushToggle";
import CoachProCard from "@/components/CoachProCard";
import QuarterlyReports from "@/components/QuarterlyReports";
import ReferralCard from "@/components/ReferralCard";
import AnnualReportCard from "@/components/AnnualReportCard";
import CoachCallsCard from "@/components/CoachCallsCard";
import SuggestedCoaches from "@/components/SuggestedCoaches";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import LoyaltyCard from "@/components/LoyaltyCard";
import { Link } from "react-router-dom";

const statusStyle = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  confirmed: "bg-blue-100 text-blue-800 border-blue-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};
const statusLabel = {
  pending: "En attente",
  confirmed: "Confirmé",
  rejected: "Refusé",
  cancelled: "Annulé",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [slots, setSlots] = useState([]);
  const [reason, setReason] = useState({});
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [slotForm, setSlotForm] = useState({ room_id: "", start: "", end: "", price: "", repeat_weeks: 0, last_minute: false });
  const [roomForm, setRoomForm] = useState({
    name: "", description: "", address: "", city: "", region: "", neighborhood: "",
    lat: 48.85, lng: 2.35, price_per_hour: 25, equipments: "", disciplines: "",
    cooperation_level: "moderate", photo_url: "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?w=800&q=80",
  });
  const [chatBookingId, setChatBookingId] = useState(null);
  const [directChat, setDirectChat] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [coachStats, setCoachStats] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [statsKey, setStatsKey] = useState(0);
  const [payBooking, setPayBooking] = useState(null);
  const [waitlist, setWaitlist] = useState([]);

  const reload = () => {
    api.get("/bookings").then((r) => setBookings(r.data));
    api.get("/conversations").then((r) => setConversations(r.data)).catch(() => {});
    setStatsKey((k) => k + 1);
    if (["coach", "client"].includes(user?.role)) api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
    if (["coach", "client"].includes(user?.role)) api.get("/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
    if (user?.role === "coach") api.get(`/coaches/${user.id}`).then((r) => setCoachStats(r.data)).catch(() => {});
    if (user?.role === "room_manager") {
      api.get("/rooms").then((r) => setRooms(r.data.filter((x) => x.manager_id === user.id)));
      api.get("/slots").then((r) => setSlots(r.data));
    }
  };
  useEffect(() => { if (user && user.id) reload(); }, [user?.id, user?.favorites?.length]);

  const validate = async (b, action) => {
    try {
      await api.post(`/bookings/${b.id}/validate`, { action, reason: reason[b.id] || "" });
      toast.success(action === "accept" ? "Réservation confirmée" : "Réservation refusée");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitSlot = async () => {
    try {
      const r = await api.post("/slots", {
        room_id: slotForm.room_id,
        start: new Date(slotForm.start).toISOString(),
        end: new Date(slotForm.end).toISOString(),
        price: slotForm.price ? Number(slotForm.price) : undefined,
        repeat_weeks: Number(slotForm.repeat_weeks) || 0,
        last_minute: !!slotForm.last_minute,
      });
      toast.success(r.data.created_count > 1 ? `${r.data.created_count} créneaux publiés (récurrence hebdomadaire)` : slotForm.last_minute ? "Créneau de dernière minute publié" : "Créneau publié");
      setShowSlotForm(false);
      setSlotForm({ room_id: "", start: "", end: "", price: "", repeat_weeks: 0, last_minute: false });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitRoom = async () => {
    try {
      await api.post("/rooms", {
        ...roomForm,
        lat: Number(roomForm.lat), lng: Number(roomForm.lng),
        price_per_hour: Number(roomForm.price_per_hour),
        equipments: roomForm.equipments.split(",").map((s) => s.trim()).filter(Boolean),
        disciplines: roomForm.disciplines.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Salle créée");
      setShowRoomForm(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const cancel = async (b) => {
    if (!window.confirm(b.paid ? "Annuler et rembourser intégralement cette séance payée ?" : "Annuler cette réservation ? Le créneau sera de nouveau libre.")) return;
    try {
      const r = await api.post(`/bookings/${b.id}/cancel`);
      toast.success(r.data.refunded ? "Réservation annulée et remboursée via Stripe" : "Réservation annulée, créneau libéré");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const validateGroup = async (b, action) => {
    try {
      const r = await api.post("/bookings/validate-group", { group_id: b.group_id, action, reason: reason[b.id] || "" });
      toast.success(action === "accept" ? `${r.data.count} créneaux confirmés d'un coup` : `${r.data.count} créneaux refusés`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const exportCsv = async () => {
    try {
      const month = new Date().toISOString().slice(0, 7);
      const r = await api.get("/manager/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `tymcoach_seances_${month}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch { toast.error("Export impossible"); }
  };

  const approveCoach = async (b) => {
    try { await api.post(`/rooms/${b.room_id}/approved-coaches/${b.coach_id}`); toast.success("Coach approuvé : il verra vos créneaux de dernière minute"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteSlot = async (id) => {
    try { await api.delete(`/slots/${id}`); toast.success("Créneau supprimé"); reload(); }
    catch (e) { toast.error("Erreur"); }
  };

  if (!user || !user.id) return null;

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sc-fade-in">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Tableau de bord</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Bonjour, {user.name.split(" ")[0]} 👋</h1>
        <p className="mt-1 text-slate-600 flex items-center gap-2">Espace {user.role === "coach" ? "Coach" : user.role === "client" ? "Particulier" : user.role === "room_manager" ? "Responsable de Salle" : "Admin"}.
          {coachStats && <VerifiedBadge coach={coachStats} />}
          {coachStats && !coachStats.verified && <span className="text-xs text-slate-500" data-testid="verified-progress">Badge vérifié : {coachStats.paid_sessions}/3 séances réglées</span>}
        </p>
        <OnboardingChecklist rooms={rooms} slots={slots} bookings={bookings} />

        {user.role === "room_manager" && (
          <div className="mt-8">
            <ManagerStats refreshKey={statsKey} />
            <div className="mt-3 flex justify-end">
              <button data-testid="export-csv-btn" onClick={exportCsv} className="sc-btn sc-btn-outline text-sm"><Download className="w-4 h-4" /> Export comptable CSV (mois en cours)</button>
            </div>
            <div className="mt-8 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Mes salles</h2>
              <button data-testid="add-room-btn" onClick={() => setShowRoomForm(true)} className="sc-btn sc-btn-emerald"><Plus className="w-4 h-4" /> Ajouter une salle</button>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rooms.map((r) => (
                <div key={r.id} className="sc-card">
                  <div className="font-bold text-slate-900 flex items-center justify-between gap-2">{r.name}<Link to={`/vitrine/${r.id}`} data-testid={`showcase-link-${r.id}`} className="text-[11px] font-semibold text-emerald-700 underline">Page vitrine</Link></div>
                  <div className="text-xs text-slate-500">{r.city}</div>
                  <div className="mt-2 text-xs">
                    Créneaux : {slots.filter((s) => s.room_id === r.id).length}
                  </div>
                  <RoomPhotosUploader room={r} onChange={reload} />
                  <ApprovedCoaches room={r} refreshKey={statsKey} />
                </div>
              ))}
              {rooms.length === 0 && <div className="sc-card text-sm text-slate-500">Aucune salle. Créez-en une !</div>}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Planning de la semaine</h2>
              <button data-testid="add-slot-btn" onClick={() => setShowSlotForm(true)} className="sc-btn sc-btn-primary"><Calendar className="w-4 h-4" /> Publier un créneau</button>
            </div>
            <WeekCalendar slots={slots} onCreate={(startLocal) => {
              const end = new Date(startLocal); end.setHours(end.getHours() + 1);
              const pad = (n) => String(n).padStart(2, "0");
              const endLocal = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`;
              setSlotForm({ room_id: rooms[0]?.id || "", start: startLocal, end: endLocal, price: "", repeat_weeks: 0, last_minute: false });
              setShowSlotForm(true);
            }} />
            <h2 className="mt-8 text-2xl font-bold text-slate-900">Créneaux publiés</h2>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {slots.map((s) => (
                <div key={s.id} className={`sc-card ${s.last_minute ? "border-amber-300 bg-amber-50/40" : ""}`}>
                  <div className="text-xs text-slate-500 flex items-center gap-1">{s.room?.name}{s.last_minute && <span data-testid={`last-minute-badge-${s.id}`} className="ml-auto text-[10px] font-bold uppercase text-amber-700 flex items-center gap-0.5"><Zap className="w-3 h-3" /> Dernière minute</span>}{s.series_id && !s.last_minute && <span className="ml-auto text-[10px] font-semibold uppercase text-indigo-600">Récurrent</span>}</div>
                  <div className="text-sm font-bold text-slate-900">{new Date(s.start).toLocaleString("fr-FR")}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusStyle[s.status] || "bg-emerald-100 text-emerald-800 border-emerald-300"}`}>{s.status}</span>
                    <button data-testid={`delete-slot-${s.id}`} onClick={() => deleteSlot(s.id)} className="text-rose-600 text-sm"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {slots.length === 0 && <div className="sc-card text-sm text-slate-500">Aucun créneau.</div>}
            </div>
          </div>
        )}

        {user.role === "coach" && <CoachLocationCard />}
        {user.role === "coach" && <CoachAvailabilityCard />}
        {user.role === "coach" && <LastMinuteSlots refreshKey={statsKey} />}
        {["coach", "client"].includes(user.role) && <CalendarSyncCard />}
        {user.role === "room_manager" && <PromoCodesCard rooms={rooms} />}
        {user.role === "room_manager" && <PushToggle description="Recevez une alerte immédiate sur ce téléphone/ordinateur pour chaque demande, surtout les urgences du jour même." />}
        {user.role === "room_manager" && <QuarterlyReports />}
        {user.role === "room_manager" && <AnnualReportCard />}
        {user.role === "room_manager" && <CoachCallsCard rooms={rooms} onChanged={reload} />}
        {user.role === "coach" && <CoachProCard />}
        {user.role === "coach" && <ReferralCard />}
        {user.role === "client" && <SuggestedCoaches />}
        {user.role === "client" && <LoyaltyCard />}
        {user.role === "coach" && <PushToggle description="Soyez alerté instantanément des créneaux de dernière minute et des réponses des salles." />}

        {["coach", "client"].includes(user.role) && waitlist.length > 0 && (
          <div className="mt-10" data-testid="waitlist-section">
            <h2 className="text-2xl font-bold text-slate-900">Mes listes d'attente</h2>
            <p className="text-sm text-slate-600 mt-1">Vous serez prévenu (notification + email) dès que l'un de ces créneaux se libère.</p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {waitlist.map((s) => (
                <div key={s.id} className="sc-card" data-testid={`waitlist-item-${s.id}`}>
                  <Link to={`/salles/${s.room?.id}`} className="font-bold text-slate-900 hover:underline">{s.room?.name}</Link>
                  <div className="text-sm text-slate-700">{new Date(s.start).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  <button data-testid={`waitlist-leave-${s.id}`} onClick={async () => { await api.delete(`/slots/${s.id}/waitlist`); toast.success("Retiré de la liste d'attente"); reload(); }} className="mt-2 text-xs text-rose-600 hover:underline">Quitter la liste</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {["coach", "client"].includes(user.role) && (
          <div className="mt-10">
            <h2 className="text-2xl font-bold text-slate-900">Mes salles favorites</h2>
            <p className="text-sm text-slate-600 mt-1">Vous êtes alerté (notification + email) dès qu'un créneau s'y libère.</p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="favorites-list">
              {favorites.map((r) => (
                <div key={r.id} className="sc-card" data-testid={`favorite-room-${r.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <Link to={`/salles/${r.id}`} className="font-bold text-slate-900 hover:underline">{r.name}</Link>
                      <div className="text-xs text-slate-500">{r.neighborhood} · {r.city} · {r.price_per_hour}€/h</div>
                    </div>
                    <FavoriteButton roomId={r.id} compact />
                  </div>
                </div>
              ))}
              {favorites.length === 0 && <div className="sc-card text-sm text-slate-500">Aucune favorite. Ajoutez-en depuis la <Link to="/carte" className="underline">carte</Link>.</div>}
            </div>
          </div>
        )}

        <h2 className="mt-10 text-2xl font-bold text-slate-900">
          {user.role === "room_manager" ? "Demandes reçues" : "Mes réservations"}
        </h2>
        <div className="mt-4 space-y-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="sc-card">
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{b.room?.name}</div>
                  <div className="font-bold text-slate-900">{b.client_name} · {b.discipline}{b.group_id && <span data-testid={`group-badge-${b.id}`} className="ml-2 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Demande groupée</span>}</div>
                  <div className="text-xs text-slate-600">{b.slot ? new Date(b.slot.start).toLocaleString("fr-FR") : ""}</div>
                  {b.notes && <div className="mt-2 text-sm text-slate-700 italic">« {b.notes} »</div>}
                  {b.reason && <div className="mt-1 text-xs text-rose-700">Motif : {b.reason}</div>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusStyle[b.status]}`}>{statusLabel[b.status]}</span>
                  {b.paid && <span data-testid={`paid-badge-${b.id}`} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Réglée</span>}
                  {!b.paid && b.status === "confirmed" && user.role !== "room_manager" && (
                    <button data-testid={`pay-${b.id}`} onClick={() => setPayBooking(b)} className="sc-btn sc-btn-emerald text-xs"><CreditCard className="w-3.5 h-3.5" /> Payer la séance</button>
                  )}
                  {!b.paid && ["pending", "confirmed"].includes(b.status) && (
                    <button data-testid={`cancel-${b.id}`} onClick={() => cancel(b)} className="sc-btn sc-btn-outline text-xs text-rose-700"><Ban className="w-3.5 h-3.5" /> Annuler</button>
                  )}
                  {b.paid && b.status === "confirmed" && user.role === "room_manager" && (
                    <button data-testid={`cancel-refund-${b.id}`} onClick={() => cancel(b)} className="sc-btn sc-btn-outline text-xs text-rose-700"><Ban className="w-3.5 h-3.5" /> Annuler & rembourser</button>
                  )}
                  {b.refunded && <span data-testid={`refunded-badge-${b.id}`} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-slate-100 text-slate-700 border-slate-300">Remboursée</span>}
                  {user.role === "room_manager" && b.coach_id && !(rooms.find((r) => r.id === b.room_id)?.approved_coaches || []).includes(b.coach_id) && (
                    <button data-testid={`approve-coach-${b.id}`} onClick={() => approveCoach(b)} className="sc-btn sc-btn-outline text-xs text-sky-700"><UserCheck className="w-3.5 h-3.5" /> Approuver ce coach</button>
                  )}
                  <button data-testid={`open-chat-${b.id}`} onClick={() => setChatBookingId(b.id)} className="sc-btn sc-btn-outline text-xs"><MessageSquare className="w-3.5 h-3.5" /> Messagerie</button>
                </div>
              </div>
              {user.role === "room_manager" && b.status === "pending" && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <input placeholder="Motif (si refus)" className="border border-slate-200 rounded-xl p-2 text-sm" value={reason[b.id] || ""} onChange={(e) => setReason({ ...reason, [b.id]: e.target.value })} data-testid={`reject-reason-${b.id}`} />
                  <button data-testid={`accept-${b.id}`} onClick={() => validate(b, "accept")} className="sc-btn sc-btn-emerald"><Check className="w-4 h-4" /> Valider</button>
                  <button data-testid={`reject-${b.id}`} onClick={() => validate(b, "reject")} className="sc-btn sc-btn-outline"><X className="w-4 h-4" /> Refuser</button>
                  {b.group_id && bookings.filter((x) => x.group_id === b.group_id && x.status === "pending").length > 1 && (
                    <>
                      <button data-testid={`accept-group-${b.id}`} onClick={() => validateGroup(b, "accept")} className="sc-btn bg-indigo-600 text-white border-0 hover:bg-indigo-500"><Check className="w-4 h-4" /> Tout valider ({bookings.filter((x) => x.group_id === b.group_id && x.status === "pending").length})</button>
                      <button data-testid={`reject-group-${b.id}`} onClick={() => validateGroup(b, "reject")} className="sc-btn sc-btn-outline text-indigo-700"><X className="w-4 h-4" /> Tout refuser</button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          {bookings.length === 0 && <div className="sc-card text-sm text-slate-500">Aucune réservation pour le moment.</div>}
        </div>

        {conversations.length > 0 && (
          <>
            <h2 className="mt-10 text-2xl font-bold text-slate-900">Messages directs</h2>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="direct-conversations">
              {conversations.map((c) => (
                <button key={c.id} data-testid={`conversation-${c.other_id}`} onClick={() => setDirectChat(c)} className="sc-card text-left hover:ring-2 hover:ring-slate-300 transition">
                  <div className="font-bold text-slate-900">{c.other_name}</div>
                  <div className="text-xs text-slate-500">{c.other_role === "coach" ? "Coach" : c.other_role === "client" ? "Particulier" : c.other_role === "room_manager" ? "Salle" : ""}</div>
                  {c.last && <div className="mt-2 text-sm text-slate-700 truncate">{c.last.from_user_id === user.id ? "Vous : " : ""}{c.last.body}</div>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {showRoomForm && (
        <Modal onClose={() => setShowRoomForm(false)} title="Nouvelle salle">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="roomform-name" placeholder="Nom" className="border rounded-xl p-2 col-span-2" value={roomForm.name} onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })} />
            <input placeholder="Ville" className="border rounded-xl p-2" value={roomForm.city} onChange={(e) => setRoomForm({ ...roomForm, city: e.target.value })} />
            <input placeholder="Région" className="border rounded-xl p-2" value={roomForm.region} onChange={(e) => setRoomForm({ ...roomForm, region: e.target.value })} />
            <input placeholder="Adresse" className="border rounded-xl p-2 col-span-2" value={roomForm.address} onChange={(e) => setRoomForm({ ...roomForm, address: e.target.value })} />
            <input placeholder="Quartier" className="border rounded-xl p-2 col-span-2" value={roomForm.neighborhood} onChange={(e) => setRoomForm({ ...roomForm, neighborhood: e.target.value })} />
            <input placeholder="Lat" type="number" step="0.0001" className="border rounded-xl p-2" value={roomForm.lat} onChange={(e) => setRoomForm({ ...roomForm, lat: e.target.value })} />
            <input placeholder="Lng" type="number" step="0.0001" className="border rounded-xl p-2" value={roomForm.lng} onChange={(e) => setRoomForm({ ...roomForm, lng: e.target.value })} />
            <input placeholder="Tarif €/h" type="number" className="border rounded-xl p-2" value={roomForm.price_per_hour} onChange={(e) => setRoomForm({ ...roomForm, price_per_hour: e.target.value })} />
            <select className="border rounded-xl p-2" value={roomForm.cooperation_level} onChange={(e) => setRoomForm({ ...roomForm, cooperation_level: e.target.value })}>
              <option value="open">Coopération ouverte</option>
              <option value="moderate">Coopération modérée</option>
              <option value="strict">Coopération stricte</option>
            </select>
            <input placeholder="Équipements (virgule)" className="border rounded-xl p-2 col-span-2" value={roomForm.equipments} onChange={(e) => setRoomForm({ ...roomForm, equipments: e.target.value })} />
            <input placeholder="Disciplines (virgule)" className="border rounded-xl p-2 col-span-2" value={roomForm.disciplines} onChange={(e) => setRoomForm({ ...roomForm, disciplines: e.target.value })} />
            <textarea placeholder="Description" className="border rounded-xl p-2 col-span-2" rows={3} value={roomForm.description} onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })} />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setShowRoomForm(false)} className="sc-btn sc-btn-outline">Annuler</button>
            <button data-testid="roomform-submit" onClick={submitRoom} className="sc-btn sc-btn-emerald">Créer la salle</button>
          </div>
        </Modal>
      )}

      {showSlotForm && (
        <Modal onClose={() => setShowSlotForm(false)} title="Nouveau créneau">
          <div className="space-y-2">
            <select data-testid="slotform-room" className="w-full border rounded-xl p-2" value={slotForm.room_id} onChange={(e) => setSlotForm({ ...slotForm, room_id: e.target.value })}>
              <option value="">Choisir une salle</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <input data-testid="slotform-start" type="datetime-local" className="w-full border rounded-xl p-2" value={slotForm.start} onChange={(e) => setSlotForm({ ...slotForm, start: e.target.value })} />
            <input data-testid="slotform-end" type="datetime-local" className="w-full border rounded-xl p-2" value={slotForm.end} onChange={(e) => setSlotForm({ ...slotForm, end: e.target.value })} />
            <input data-testid="slotform-price" type="number" placeholder="Prix €/h (optionnel)" className="w-full border rounded-xl p-2" value={slotForm.price} onChange={(e) => setSlotForm({ ...slotForm, price: e.target.value })} />
            <div className="grid grid-cols-2 gap-2 pt-1">
              <label className="text-sm text-slate-700">
                <span className="block text-xs font-semibold text-slate-500 mb-1">Répéter chaque semaine</span>
                <select data-testid="slotform-repeat" className="w-full border rounded-xl p-2" value={slotForm.repeat_weeks} onChange={(e) => setSlotForm({ ...slotForm, repeat_weeks: e.target.value })}>
                  <option value={0}>Une seule fois</option>
                  {[1, 2, 3, 4, 8, 12].map((n) => <option key={n} value={n}>+{n} semaine{n > 1 ? "s" : ""} ({n + 1} créneaux)</option>)}
                </select>
              </label>
              <label data-testid="slotform-last-minute" className={`flex items-center gap-2 border rounded-xl p-2 text-sm cursor-pointer ${slotForm.last_minute ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-700"}`}>
                <input type="checkbox" className="accent-amber-500" checked={slotForm.last_minute} onChange={(e) => setSlotForm({ ...slotForm, last_minute: e.target.checked })} />
                <span><Zap className="w-3.5 h-3.5 inline" /> Dernière minute<span className="block text-[11px] font-normal text-slate-500">Visible seulement par vos coachs approuvés</span></span>
              </label>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setShowSlotForm(false)} className="sc-btn sc-btn-outline">Annuler</button>
            <button data-testid="slotform-submit" onClick={submitSlot} className="sc-btn sc-btn-primary">Publier</button>
          </div>
        </Modal>
      )}

      {payBooking && <PayModal booking={payBooking} onClose={() => setPayBooking(null)} />}
      {chatBookingId && (
        <MessagingDrawer bookingId={chatBookingId} onClose={() => setChatBookingId(null)} bookings={bookings} />
      )}
      {directChat && (
        <MessagingDrawer bookingId={directChat.id} otherId={directChat.other_id} title={`Conversation avec ${directChat.other_name}`} onClose={() => { setDirectChat(null); reload(); }} />
      )}

      <Footer />
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="font-bold text-slate-900 text-lg mb-3">{title}</div>
        {children}
      </div>
    </div>
  );
}
